import React, { useState, useEffect } from 'react';
import {
  createResumeEntry,
  updateResumeEntry,
  deleteResumeEntry,
} from '../../api/api';
import './ResumeModal.css';

const ResumeModal = ({
  entry,
  onClose,
  onSave,
  entries = [],
  mode = 'add',
  categoryForSelection = null,
  onSelectEdit = () => {},
}) => {
  const isEdit = !!entry?.id && mode === 'edit';
  const isSelectionMode = mode === 'select-edit' || mode === 'select-delete';

  const initialForm = {
    category: entry?.category || categoryForSelection || 'education',
    title: entry?.title || '',
    subtitle: entry?.subtitle || '',
    description: entry?.description || '',
    startDate: entry?.startDate
      ? new Date(entry.startDate).toISOString().split('T')[0]
      : '',
    endDate: entry?.endDate
      ? new Date(entry.endDate).toISOString().split('T')[0]
      : '',
    institution: entry?.institution || '',
    issuer: entry?.issuer || '',
    level: entry?.level || '',
    url: entry?.url || '',
    icon: null,
  };

  const [formData, setFormData] = useState(initialForm);
  const [previewIcon, setPreviewIcon] = useState(entry?.iconUrl || '');
  const [selectedId, setSelectedId] = useState('');

  useEffect(() => {
    if (entry?.iconUrl) setPreviewIcon(entry.iconUrl);

    if (entry) {
      setFormData({
        category: entry.category || categoryForSelection || 'education',
        title: entry.title || '',
        subtitle: entry.subtitle || '',
        description: entry.description || '',
        startDate: entry.startDate
          ? new Date(entry.startDate).toISOString().split('T')[0]
          : '',
        endDate: entry.endDate
          ? new Date(entry.endDate).toISOString().split('T')[0]
          : '',
        institution: entry.institution || '',
        issuer: entry.issuer || '',
        level: entry.level || '',
        url: entry.url || '',
        icon: null,
      });
    }
  }, [entry, categoryForSelection]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'icon' && files?.[0]) {
      const file = files[0];
      setFormData((prev) => ({ ...prev, icon: file }));
      setPreviewIcon(URL.createObjectURL(file));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = new FormData();
    data.append('category', formData.category);
    data.append('title', formData.title);

    if (
      ['education', 'experience', 'achievements'].includes(formData.category)
    ) {
      data.append('subtitle', formData.subtitle || '');
      data.append('description', formData.description || '');
      data.append('url', formData.url || '');
    }

    if (['education', 'experience'].includes(formData.category)) {
      data.append('startDate', formData.startDate || '');
      data.append('endDate', formData.endDate || '');
      data.append('institution', formData.institution || '');
    }

    if (formData.category === 'achievements') {
      data.append('issuer', formData.issuer || '');
    }

    if (['skills', 'languages'].includes(formData.category)) {
      data.append('level', formData.level || '');
    }

    if (!['languages'].includes(formData.category) && formData.icon) {
      data.append('icon', formData.icon);
    }

    try {
      let result;
      if (isEdit) {
        //console.log('UPDATE ID:', entry.id, typeof entry.id);
        //console.log('CATEGORY:', formData.category);

        result = await updateResumeEntry(Number(entry.id), data);
      } else {
        result = await createResumeEntry(data);
      }
      onSave(result);
      onClose();
    } catch (err) {
      console.error('Błąd zapisu:', err);
      alert('Błąd zapisu. Spróbuj ponownie.');
    }
  };

  const handleDeleteSelected = async () => {
    if (!selectedId) return alert('Wybierz wpis do usunięcia');

    // Konwersja na liczbę
    const id = selectedId;
    const selected = entries.find((e) => String(e.id) === String(id));

    if (!selected) {
      //console.log('Nie znaleziono wpisu. ID:', id, 'Lista:', entries);
      return alert(`Nie znaleziono wpisu o ID: ${id}`);
    }

    if (!window.confirm(`Na pewno chcesz usunąć "${selected.title}"?`)) return;

    try {
      await deleteResumeEntry(id);
      onSave({ deletedId: Number(id) });
      alert('Usunięto pomyślnie');
      onClose();
    } catch (err) {
      alert('Błąd usuwania: ' + err.message);
    }
  };

  const handleEditSelected = () => {
    if (!selectedId) return alert('Wybierz wpis do edycji');

    const id = selectedId;
    const selected = entries.find((e) => String(e.id) === String(id));

    if (!selected) {
      //console.log('Nie znaleziono wpisu do edycji. ID:', id, 'Lista:', entries);
      return alert(`Nie znaleziono wpisu o ID: ${id}`);
    }

    onSelectEdit(selected);
    onClose();
  };

  if (isSelectionMode) {
    const filteredEntries = entries.filter(
      (e) => e.category === categoryForSelection
    );

    return (
      <div className="resume-modal-overlay" onClick={onClose}>
        <div
          className="resume-modal-content"
          onClick={(e) => e.stopPropagation()}
        >
          <button className="modal-close-btn" onClick={onClose}>
            ×
          </button>

          <h2>
            {mode === 'select-edit'
              ? 'Wybierz wpis do edycji'
              : 'Wybierz wpis do usunięcia'}
          </h2>

          {filteredEntries.length === 0 ? (
            <p>Brak wpisów w tej kategorii</p>
          ) : (
            <div className="form-group">
              <label>Wybierz wpis</label>
              <select
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
              >
                <option value="">— wybierz —</option>
                {filteredEntries.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.title} {item.level ? `(${item.level})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="modal-actions">
            {mode === 'select-edit' ? (
              <button
                type="button"
                className="save-btn"
                disabled={!selectedId}
                onClick={handleEditSelected}
              >
                Edytuj wybrany
              </button>
            ) : (
              <button
                type="button"
                className="delete-btn"
                disabled={!selectedId}
                onClick={handleDeleteSelected}
              >
                Usuń wybrany
              </button>
            )}
            <button type="button" className="cancel-btn" onClick={onClose}>
              Anuluj
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="resume-modal-overlay" onClick={onClose}>
      <div
        className="resume-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close-btn" onClick={onClose}>
          ×
        </button>

        <h2>{isEdit ? 'Edytuj wpis' : 'Dodaj nowy wpis'}</h2>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Kategoria *</label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              required
              disabled={isEdit}
            >
              <option value="">Wybierz...</option>
              <option value="education">Edukacja</option>
              <option value="experience">Doświadczenie</option>
              <option value="achievements">Osiągnięcia</option>
              <option value="skills">Umiejętności</option>
              <option value="languages">Języki</option>
            </select>
          </div>

          <div className="form-group">
            <label>
              {formData.category === 'languages'
                ? 'Nazwa języka *'
                : formData.category === 'skills'
                  ? 'Nazwa umiejętności *'
                  : 'Tytuł *'}
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              placeholder={
                formData.category === 'languages'
                  ? 'np. Angielski, Polski'
                  : formData.category === 'skills'
                    ? 'np. React.js, Node.js'
                    : 'Tytuł'
              }
            />
          </div>

          {formData.category === 'skills' && (
            <div className="form-group">
              <label>Ikona / Logo (opcjonalna)</label>
              <input
                type="file"
                name="icon"
                accept="image/*"
                onChange={handleChange}
              />
              {previewIcon && (
                <div className="preview-icon">
                  <img src={previewIcon} alt="Podgląd ikony" />
                </div>
              )}
            </div>
          )}

          {formData.category === 'languages' && (
            <div className="form-group">
              <label>Poziom *</label>
              <input
                type="text"
                name="level"
                value={formData.level}
                onChange={handleChange}
                required
                placeholder="np. B2, C1, native, zaawansowany"
              />
            </div>
          )}

          {/* Pozostałe pola dla education, experience, achievements – bez zmian */}
          {['education', 'experience'].includes(formData.category) && (
            <>
              <div className="form-group">
                <label>Podtytuł / Stanowisko / Kierunek</label>
                <input
                  type="text"
                  name="subtitle"
                  value={formData.subtitle}
                  onChange={handleChange}
                  placeholder={
                    formData.category === 'education'
                      ? 'Kierunek studiów'
                      : 'Stanowisko'
                  }
                />
              </div>

              <div className="form-group">
                <label>Opis</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={5}
                  placeholder="Szczegółowy opis..."
                />
              </div>

              <div className="form-group-row">
                <div className="form-group half">
                  <label>Data rozpoczęcia</label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group half">
                  <label>Data zakończenia</label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>
                  {formData.category === 'education'
                    ? 'Uczelnia / Szkoła'
                    : 'Firma / Pracodawca'}
                </label>
                <input
                  type="text"
                  name="institution"
                  value={formData.institution}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Link (opcjonalny)</label>
                <input
                  type="url"
                  name="url"
                  value={formData.url}
                  onChange={handleChange}
                  placeholder="https://..."
                />
              </div>

              <div className="form-group">
                <label>Logo / Zdjęcie (opcjonalne)</label>
                <input
                  type="file"
                  name="icon"
                  accept="image/*"
                  onChange={handleChange}
                />
                {previewIcon && (
                  <div className="preview-icon">
                    <img src={previewIcon} alt="Podgląd" />
                  </div>
                )}
              </div>
            </>
          )}

          {formData.category === 'achievements' && (
            <>
              <div className="form-group">
                <label>Podtytuł / Osiągnięcie dodatkowe</label>
                <input
                  type="text"
                  name="subtitle"
                  value={formData.subtitle}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Opis</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={5}
                  placeholder="Szczegółowy opis osiągnięcia..."
                />
              </div>

              <div className="form-group">
                <label>Wydawca / Organizator</label>
                <input
                  type="text"
                  name="issuer"
                  value={formData.issuer}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Link (opcjonalny)</label>
                <input
                  type="url"
                  name="url"
                  value={formData.url}
                  onChange={handleChange}
                  placeholder="https://..."
                />
              </div>

              <div className="form-group">
                <label>Zdjęcie / Certyfikat (opcjonalne)</label>
                <input
                  type="file"
                  name="icon"
                  accept="image/*"
                  onChange={handleChange}
                />
                {previewIcon && (
                  <div className="preview-icon">
                    <img src={previewIcon} alt="Podgląd" />
                  </div>
                )}
              </div>
            </>
          )}

          <div className="modal-actions">
            <button type="submit" className="save-btn">
              {isEdit ? 'Zapisz zmiany' : 'Dodaj wpis'}
            </button>
            <button type="button" className="cancel-btn" onClick={onClose}>
              Anuluj
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResumeModal;
